<template>
  <div class="AvatarNotificationBubble">
    <!-- Main Avatar Content (passed through slot) -->
    <div class="AvatarNotificationBubble__avatar-container">
      <slot></slot>
      
      <!-- Notification Bubble -->
      <div 
        v-if="showNotification"
        class="AvatarNotificationBubble__bubble"
        :class="{ 
          'AvatarNotificationBubble__bubble--appear': bubbleVisible,
          'AvatarNotificationBubble__bubble--disappear': bubbleDisappearing 
        }"
      >
        <!-- Badge Icon -->
        <div class="AvatarNotificationBubble__badge-icon">
          <img 
            :src="badgeImage" 
            alt="Achievement Badge"
            class="AvatarNotificationBubble__badge-image"
          />
        </div>
        
        <!-- Pulse Rings -->
        <div class="AvatarNotificationBubble__pulse-ring AvatarNotificationBubble__pulse-ring--1"></div>
        <div class="AvatarNotificationBubble__pulse-ring AvatarNotificationBubble__pulse-ring--2"></div>
        
        <!-- Pop Effect -->
        <div class="AvatarNotificationBubble__pop-effect"></div>
      </div>
    </div>
    
    <!-- Tooltip/Hint -->
    <div 
      v-if="showTooltip"
      class="AvatarNotificationBubble__tooltip"
      :class="{ 'AvatarNotificationBubble__tooltip--visible': tooltipVisible }"
    >
      <div class="AvatarNotificationBubble__tooltip-content">
        <v-icon small class="mr-1">mdi-trophy</v-icon>
        Badge earned! Check your profile
      </div>
      <div class="AvatarNotificationBubble__tooltip-arrow"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AvatarNotificationBubble',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    badgeImage: {
      type: String,
      default: require('@/assets/forge-badge.png')
    },
    duration: {
      type: Number,
      default: 4000 // Total duration in milliseconds
    },
    showTooltip: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showNotification: false,
      bubbleVisible: false,
      bubbleDisappearing: false,
      tooltipVisible: false,
      animationTimeout: null,
      sequenceTimeouts: []
    }
  },
  watch: {
    show: {
      handler(newVal) {
        if (newVal) {
          this.startNotificationSequence()
        } else {
          this.hideNotification()
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.clearAllTimeouts()
  },
  methods: {
    startNotificationSequence() {
      this.clearAllTimeouts()
      this.showNotification = true
      
      // Step 1: Show bubble with scale-in animation
      this.sequenceTimeouts.push(setTimeout(() => {
        this.bubbleVisible = true
      }, 100))
      
      // Step 2: Show tooltip after bubble is visible
      if (this.showTooltip) {
        this.sequenceTimeouts.push(setTimeout(() => {
          this.tooltipVisible = true
        }, 800))
      }
      
      // Step 3: Start disappearing animation
      this.sequenceTimeouts.push(setTimeout(() => {
        this.bubbleDisappearing = true
        this.tooltipVisible = false
      }, this.duration - 800))
      
      // Step 4: Hide notification completely
      this.sequenceTimeouts.push(setTimeout(() => {
        this.hideNotification()
        this.$emit('notification-complete')
      }, this.duration))
    },
    
    hideNotification() {
      this.showNotification = false
      this.bubbleVisible = false
      this.bubbleDisappearing = false
      this.tooltipVisible = false
      this.clearAllTimeouts()
    },
    
    clearAllTimeouts() {
      this.sequenceTimeouts.forEach(timeout => clearTimeout(timeout))
      this.sequenceTimeouts = []
      if (this.animationTimeout) {
        clearTimeout(this.animationTimeout)
        this.animationTimeout = null
      }
    },
    
    // Manual trigger for testing
    triggerNotification() {
      this.startNotificationSequence()
    }
  }
}
</script>

<style lang="scss">
$style: AvatarNotificationBubble;

.#{$style} {
  position: relative;
  display: inline-block;
  
  &__avatar-container {
    position: relative;
    display: inline-block;
  }
  
  &__bubble {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(45deg, $Y1, lighten($Y1, 10%));
    border: 2px solid $white;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba($Y1, 0.3);
    opacity: 0;
    transform: scale(0);
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    
    &--appear {
      opacity: 1;
      transform: scale(1);
    }
    
    &--disappear {
      opacity: 0;
      transform: scale(1.2);
      transition: all 0.6s ease-out;
      
      .#{$style}__pulse-ring {
        animation-play-state: paused;
        opacity: 0;
      }
      
      .#{$style}__pop-effect {
        opacity: 1;
        transform: scale(2.5);
      }
    }
  }
  
  &__badge-icon {
    position: relative;
    z-index: 2;
  }
  
  &__badge-image {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    object-fit: cover;
  }
  
  &__pulse-ring {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: 1px solid rgba($Y1, 0.6);
    border-radius: 50%;
    animation: pulse-ring 2s ease-out infinite;
    
    &--1 {
      width: 24px;
      height: 24px;
      animation-delay: 0s;
    }
    
    &--2 {
      width: 32px;
      height: 32px;
      animation-delay: 1s;
    }
  }
  
  &__pop-effect {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(45deg, transparent, rgba($Y1, 0.3), transparent);
    border: 1px solid rgba($Y1, 0.4);
    opacity: 0;
    transition: all 0.6s ease-out;
  }
  
  &__tooltip {
    position: fixed;
    top: 70px; // Position below the app bar
    right: 20px; // Align with right edge, ensuring it stays in viewport
    background: rgba($N4, 0.95);
    border: 1px solid $N5;
    border-radius: 6px;
    padding: 8px 12px;
    white-space: nowrap;
    z-index: 999;
    opacity: 0;
    transform: translateY(-10px);
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    max-width: 250px; // Prevent tooltip from being too wide
    
    &--visible {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &__tooltip-content {
    font-size: $H11;
    color: $white;
    font-weight: 500;
    display: flex;
    align-items: center;
  }
  
  &__tooltip-arrow {
    position: absolute;
    top: -4px;
    right: 30px; // Position arrow near the right side to align with avatar
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid rgba($N4, 0.95);
  }
}

// Keyframe Animations
@keyframes pulse-ring {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
}

// Responsive adjustments for mobile
@media screen and (max-width: 600px) {
  .#{$style} {
    &__tooltip {
      top: 60px; // Adjust for smaller mobile app bar
      right: 10px; // Less padding on mobile
      font-size: $H10;
      padding: 6px 10px;
      max-width: 200px; // Smaller max width on mobile
    }
    
    &__tooltip-arrow {
      right: 20px; // Adjust arrow position for mobile
    }
    
    &__bubble {
      width: 18px;
      height: 18px;
      top: -6px;
      right: -6px;
    }
    
    &__badge-image {
      width: 10px;
      height: 10px;
    }
  }
}
</style>
